import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield, TrendingUp, BarChart3, FileText, Brain, Users, Lock, CreditCard, Wallet, CheckCircle, Sparkles, Mail, Phone, MapPin, Zap } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Wallet className="h-6 w-6" />,
      title: 'All Accounts in One Place',
      description: 'Connect multiple bank accounts, credit cards, and wallets. See your complete financial picture in a single dashboard.',
      color: 'bg-blue-500',
    },
    {
      icon: <Brain className="h-6 w-6" />,
      title: 'AI-Powered Categorization',
      description: 'Import statements from any bank and let AI automatically categorize transactions. Works with all major Australian banks.',
      color: 'bg-purple-500',
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: 'Spending Insights',
      description: 'Discover where your money actually goes. Get personalized insights and actionable tips to save more.',
      color: 'bg-green-500',
    },
    {
      icon: <CreditCard className="h-6 w-6" />,
      title: 'Track Every Card & Account',
      description: 'Got multiple credit cards and savings accounts? Import them all. Finally see the full picture of your finances.',
      color: 'bg-orange-500',
    },
    {
      icon: <Shield className="h-6 w-6" />,
      title: 'Bank-Level Security',
      description: 'Your financial data is protected with enterprise-grade encryption. We never sell or share your data.',
      color: 'bg-emerald-500',
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Perfect for Everyone',
      description: 'Whether managing household expenses as a family or tracking personal spending solo, our tools adapt to you.',
      color: 'bg-pink-500',
    },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Top Navigation Bar with Logo */}
      <nav className="bg-[#3d3d6b] shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="/logo-banner.png" 
                alt="My Budget Tracker" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/login')}
                className="text-white hover:bg-white/10"
                data-testid="nav-sign-in"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/pricing')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                data-testid="nav-subscribe-now"
              >
                <Zap className="h-4 w-4 mr-2" />
                Subscribe Now
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Gradient */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50"></div>
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-30">
          <div className="absolute top-20 right-20 w-72 h-72 bg-blue-400 rounded-full filter blur-3xl"></div>
          <div className="absolute top-40 right-40 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-64 h-64 bg-pink-400 rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                Start your 14-day free trial — No credit card required!
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                After trial: $14.99/month or $149.99/year
              </p>
              <p className="text-xs text-muted-foreground mb-4 max-w-md">
                Covers website hosting, maintenance & AI-powered categorization
              </p>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight text-foreground mb-6 leading-tight">
                See Where Your Money Goes—
                <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Across All Your Accounts
                </span>
              </h1>
              <p className="text-xl font-body text-muted-foreground mb-4 leading-relaxed">
                For <strong className="text-foreground">families</strong> and <strong className="text-foreground">individuals</strong> who want clarity over their finances.
              </p>
              <p className="text-lg font-body text-muted-foreground mb-8 leading-relaxed">
                Track spending across multiple bank accounts and credit cards. Import your statements and let AI do the rest.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="h-14 px-8 text-base bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  data-testid="get-started-button"
                >
                  Start Tracking Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="h-14 px-8 text-base border-2"
                  data-testid="sign-in-button"
                >
                  Sign In
                </Button>
              </div>
              <div className="mt-6 flex items-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  No credit card
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Bank-level security
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Cancel anytime
                </span>
              </div>
              
              {/* Download App Buttons */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-sm font-medium text-muted-foreground mb-3">Get the app on your device:</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => {
                      if (window.deferredPrompt) {
                        window.deferredPrompt.prompt();
                      } else {
                        alert('To install: Click the menu button in your browser and select "Add to Home Screen" or "Install App"');
                      }
                    }}
                    className="flex items-center justify-center gap-3 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                    data-testid="download-android-btn"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.523 15.341c-.5 0-.5-.5-.5-.5v-6c0-.5.5-.5.5-.5s.5 0 .5.5v6c0 .5-.5.5-.5.5zm-11 0c-.5 0-.5-.5-.5-.5v-6c0-.5.5-.5.5-.5s.5 0 .5.5v6c0 .5-.5.5-.5.5zm11.5-9.5l1.5-2.5c.1-.2 0-.4-.2-.5-.2-.1-.4 0-.5.2l-1.5 2.5c-1.1-.5-2.3-.7-3.6-.7s-2.5.3-3.6.7l-1.5-2.5c-.1-.2-.3-.3-.5-.2-.2.1-.3.3-.2.5l1.5 2.5c-2.5 1.2-4.2 3.6-4.4 6.5h16.5c-.3-2.9-2-5.3-4.5-6.5zm-8.5 4c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm6 0c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm-9 3v7c0 .5.5 1 1 1h1v2.5c0 .5.5.5.5.5s.5 0 .5-.5v-2.5h2v2.5c0 .5.5.5.5.5s.5 0 .5-.5v-2.5h1c.5 0 1-.5 1-1v-7h-8z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs opacity-80">Download for</div>
                      <div className="text-sm font-semibold">Android</div>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      if (window.deferredPrompt) {
                        window.deferredPrompt.prompt();
                      } else {
                        alert('To install on iOS: Tap the Share button, then select "Add to Home Screen"');
                      }
                    }}
                    className="flex items-center justify-center gap-3 bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                    data-testid="download-ios-btn"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs opacity-80">Download for</div>
                      <div className="text-sm font-semibold">iOS</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Hero Image Card */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl transform rotate-3"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-4">
                  <span className="font-heading text-lg font-semibold">All Accounts Overview</span>
                  <span className="text-xs font-mono text-muted-foreground bg-gray-100 px-2 py-1 rounded">FEB 2026</span>
                </div>
                <div className="space-y-3 pb-4 border-b border-gray-100 mb-4">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-red-600">W</span>
                      </div>
                      <span className="text-sm">Westpac Everyday</span>
                    </div>
                    <span className="font-mono font-medium">$2,450</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-yellow-600">C</span>
                      </div>
                      <span className="text-sm">CommBank Savings</span>
                    </div>
                    <span className="font-mono font-medium">$8,200</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-blue-600">V</span>
                      </div>
                      <span className="text-sm">Visa Credit Card</span>
                    </div>
                    <span className="font-mono font-medium text-red-600">-$1,240</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                    <p className="text-xs font-medium text-green-600 uppercase mb-1">Total Income</p>
                    <p className="text-2xl font-heading font-bold text-green-700">$5,240</p>
                  </div>
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4">
                    <p className="text-xs font-medium text-red-600 uppercase mb-1">Total Spent</p>
                    <p className="text-2xl font-heading font-bold text-red-700">$3,876</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-4 text-white">
                  <p className="text-xs font-medium uppercase opacity-80 mb-1">Net Worth</p>
                  <p className="text-3xl font-heading font-bold">$9,410</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Bar */}
      <div className="bg-white border-y border-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 text-center">
            <div>
              <p className="text-3xl font-heading font-bold text-foreground">10,000+</p>
              <p className="text-sm text-muted-foreground">Happy Users</p>
            </div>
            <div className="h-12 w-px bg-gray-200 hidden md:block"></div>
            <div>
              <p className="text-3xl font-heading font-bold text-foreground">$2M+</p>
              <p className="text-sm text-muted-foreground">Tracked Monthly</p>
            </div>
            <div className="h-12 w-px bg-gray-200 hidden md:block"></div>
            <div>
              <p className="text-3xl font-heading font-bold text-foreground">4.9/5</p>
              <p className="text-sm text-muted-foreground">User Rating</p>
            </div>
            <div className="h-12 w-px bg-gray-200 hidden md:block"></div>
            <div>
              <p className="text-3xl font-heading font-bold text-foreground">14-Day</p>
              <p className="text-sm text-muted-foreground">Free Trial</p>
            </div>
          </div>
        </div>
      </div>

      {/* Who Is This For Section with Images */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-foreground mb-4">
              Built For People Like You
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Whether you're managing a family budget or building personal wealth
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="group relative overflow-hidden rounded-2xl">
              <img 
                src="https://images.unsplash.com/photo-1767239650392-1f73d63b5652?w=600&h=400&fit=crop" 
                alt="Happy family" 
                className="w-full h-64 object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">Families</span>
                </div>
                <p className="text-sm opacity-90">
                  Track shared expenses, set family budgets, and make financial decisions together with complete visibility.
                </p>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl">
              <img 
                src="https://images.unsplash.com/photo-1758526214018-a746f9554b8b?w=600&h=400&fit=crop" 
                alt="Person using banking app" 
                className="w-full h-64 object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="h-5 w-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">Multiple Account Holders</span>
                </div>
                <p className="text-sm opacity-90">
                  See all your finances in one place. No more logging into 5 different banking apps.
                </p>
              </div>
            </div>
            <div className="group relative overflow-hidden rounded-2xl">
              <img 
                src="https://images.pexels.com/photos/1602726/pexels-photo-1602726.jpeg?w=600&h=400&fit=crop" 
                alt="Savings piggy bank" 
                className="w-full h-64 object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5" />
                  <span className="text-sm font-medium uppercase tracking-wider">Wealth Builders</span>
                </div>
                <p className="text-sm opacity-90">
                  Track spending habits, cut back on waste, and watch your savings grow month by month.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section with Colorful Cards */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-foreground mb-4">
              Everything You Need to Master Your Money
            </h2>
            <p className="text-lg font-body text-muted-foreground max-w-2xl mx-auto">
              Professional tools designed for real people. Simple, powerful, and beautiful.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                data-testid={`feature-${index}`}
              >
                <div className={`h-12 w-12 rounded-xl ${feature.color} flex items-center justify-center mb-4 text-white shadow-lg`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm font-body text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-20 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight mb-4">
              Get Started in 3 Simple Steps
            </h2>
            <p className="text-lg opacity-90">
              From signup to insights in under 5 minutes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm text-3xl font-heading font-bold mb-6 border border-white/30">
                1
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">Create Free Account</h3>
              <p className="text-sm opacity-80">
                Sign up in 30 seconds. No credit card, no commitments. Works for individuals and families.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm text-3xl font-heading font-bold mb-6 border border-white/30">
                2
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">Import Your Accounts</h3>
              <p className="text-sm opacity-80">
                Upload statements from all your banks and cards. AI automatically categorizes everything.
              </p>
            </div>
            <div className="text-center">
              <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm text-3xl font-heading font-bold mb-6 border border-white/30">
                3
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">See the Full Picture</h3>
              <p className="text-sm opacity-80">
                View all spending in one dashboard. Set budgets. Get insights. Build wealth.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Security Section with Visual */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
                <Shield className="h-4 w-4" />
                Bank-Level Security
              </div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-foreground mb-6">
                Your Data is Safe with Us
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">Bank-grade encryption</h3>
                    <p className="text-sm text-muted-foreground">All data encrypted at rest and in transit using industry-standard protocols</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">Your data stays yours</h3>
                    <p className="text-sm text-muted-foreground">We never sell, share, or use your financial information for any purpose other than providing you service</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">GDPR & Australian Privacy Act compliant</h3>
                    <p className="text-sm text-muted-foreground">Full control over your data with ability to export or delete anytime</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">Activity monitoring</h3>
                    <p className="text-sm text-muted-foreground">Track all account activity and get notified of any suspicious actions</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-600 rounded-3xl transform rotate-3 opacity-20"></div>
              <img 
                src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=500&fit=crop" 
                alt="Secure banking" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/10 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white/10 rounded-full filter blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center text-white">
          <h2 className="text-4xl lg:text-5xl font-heading font-bold tracking-tight mb-6">
            Ready to See Where Your Money Goes?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of Australians who finally have clarity over their finances. Start tracking for free today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/login')}
              className="h-14 px-10 text-lg bg-white text-purple-600 hover:bg-gray-100"
              data-testid="bottom-cta-button"
            >
              Start Tracking for Free
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
          </div>
          <p className="mt-4 text-sm opacity-80">
            14-day free trial • No credit card required • Cancel anytime
          </p>
          <p className="mt-2 text-sm opacity-70">
            After trial: $14.99 per month or $149.99 per annum
          </p>
          <p className="mt-3 text-xs opacity-60 max-w-lg mx-auto">
            Your subscription helps cover ongoing website maintenance, secure hosting, and our AI-powered transaction categorization that makes tracking your finances effortless.
          </p>
        </div>
      </div>

      {/* Contact Us Section */}
      <div className="py-20 bg-white" id="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-foreground mb-4">
                Get in Touch
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Have questions about My Budget Tracker? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Mail className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">Email Us</h3>
                    <p className="text-muted-foreground">support@mybudgettracker.com.au</p>
                    <p className="text-sm text-muted-foreground mt-1">We typically respond within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground">Location</h3>
                    <p className="text-muted-foreground">Sydney, NSW, Australia</p>
                    <p className="text-sm text-muted-foreground mt-1">Proudly Australian owned and operated</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-2xl p-8">
              <h3 className="text-xl font-heading font-semibold text-foreground mb-6">Send us a message</h3>
              <form className="space-y-4" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">First Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      placeholder="John"
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Last Name</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                      placeholder="Smith"
                      data-testid="contact-last-name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Email</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                    placeholder="john@example.com"
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Subject</label>
                  <select 
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white"
                    data-testid="contact-subject"
                  >
                    <option value="">Select a topic</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Technical Support</option>
                    <option value="billing">Billing Question</option>
                    <option value="feedback">Feedback</option>
                    <option value="partnership">Partnership Opportunity</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Message</label>
                  <textarea 
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all resize-none"
                    placeholder="How can we help you?"
                    data-testid="contact-message"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  data-testid="contact-submit"
                >
                  Send Message
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm font-body text-muted-foreground">
              © 2026 My Budget Tracker. Built with care for families who value their financial future.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-sm font-body text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm font-body text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-terms-link">
                Terms of Service
              </Link>
              <Link to="/pricing" className="text-sm font-body text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-pricing-link">
                Pricing
              </Link>
              <a href="#contact" className="text-sm font-body text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-contact-link">
                Contact Us
              </a>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs font-body text-muted-foreground text-center">
                          My Budget Tracker is a personal finance management tool and does not provide financial, investment, or tax advice. 
            Always consult qualified professionals for financial decisions. Your data is encrypted and secure.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
